import type { ReactElement } from 'react';
import { ErrorCode } from '@gr/core/errors';
import { useT } from '../../../../app/di';
import { Button } from '../../../../ui-kit/Button/Button';
import { FormAlert, FormCheckbox, FormMarkdownField, FormTextField, useServerForm } from '../../../../ui-kit/form/form';
import type { ServerOutcome } from '../../../../ui-kit/form/server-errors';
import { POST_FORMAT_FIELDS, type PostFormatValues } from '../model/post-formats';

export interface PostFormatFormProps {
  initial: PostFormatValues;
  /** Правило уже по умолчанию — отметку снимают, только отметив другое правило (POST-UC-10.CC-1). */
  defaultLocked: boolean;
  submitLabel: string;
  onSubmit(values: PostFormatValues): Promise<ServerOutcome>;
  onCancel?(): void;
}

/** POST-UC-10, POST-UC-10.CC-1: тип правила, отметка по умолчанию и текст в Markdown с предпросмотром. */
export function PostFormatForm({ initial, defaultLocked, submitLabel, onSubmit, onCancel }: PostFormatFormProps): ReactElement {
  const { t } = useT();
  const { form, submit } = useServerForm<PostFormatValues>({ defaultValues: initial, fields: POST_FORMAT_FIELDS, onSubmit });
  const { control, formState } = form;
  const busy = formState.isSubmitting;
  return (
    <form className="u-stack" onSubmit={submit} noValidate>
      <FormAlert form={form} />
      <FormTextField control={control} name="name" label={t('posts.formats.name')} hint={t('posts.formats.nameHint')} rules={{ required: ErrorCode.FieldRequired }} required disabled={busy} />
      <div className="u-stack">
        <FormCheckbox control={control} name="isDefault" label={t('posts.formats.isDefault')} disabled={busy || defaultLocked} />
        {defaultLocked ? <p className="u-muted u-small">{t('posts.formats.defaultLocked')}</p> : null}
      </div>
      <FormMarkdownField
        control={control}
        name="rules"
        label={t('posts.formats.rules')}
        hint={t('posts.formats.rulesHint')}
        rows={14}
        previewLabel={t('posts.editor.preview')}
        emptyPreview={t('posts.editor.previewEmpty')}
        disabled={busy}
      />
      <div className="u-row">
        <Button type="submit" variant="primary" loading={busy}>
          {submitLabel}
        </Button>
        {onCancel ? (
          <Button onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </Button>
        ) : null}
      </div>
    </form>
  );
}
